import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { retry, takeUntil } from 'rxjs/operators';
import { UserApp } from 'app/models';
import { SnackBarService } from '../snackbar-service/snackbar.service';
import { AppUsersService } from '../app-users-service/app-users.service';
import { SnackbarType } from 'app/constants/Enums';
import { ApiUrl } from 'app/constants/ApiUrls';

@Injectable()
export class UserAppsService implements OnDestroy {
  readonly userAppsUrl = ApiUrl.usersV2;

  private _userApps: Subject<UserApp[]> = new Subject<
    UserApp[]
  >();
  public userApps$: Observable<any> = this._userApps.asObservable();

  private _error: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public error$: Observable<string> = this._error.asObservable();

  private onDestroy$ = new Subject<void>();
  public loading: boolean = false;

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    public _snackbarService: SnackBarService,
    private _appUserServ: AppUsersService
  ) {}

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  getApps(userId: string): void {
    this._error.next(null);
    this.loadApps(userId);
  }

  loadApps(userId: string): void {
    this._http.get(`${this.userAppsUrl}/${userId}/Apps`).subscribe(
      data => this._userApps.next(data as UserApp[]),
      error => this._handleUserAppsError(error)
    );
  }

  private _handleUserAppsError(error: HttpErrorResponse): void {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[user-apps-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[user-apps-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    this._userApps.error(errorMsg);
  }

  clearSubscription(): void {
    this._userApps.next(null);
  }

  saveApps(
    userId: string,
    apps: UserApp[],
    ignoreMessage?: boolean,
    dialog?: any,
    userName?: any,
    clientId?: any
  ): void {
    this.loading = true;
    this._error.next(null);
    this._http
      .patch(`${this.userAppsUrl}/${userId}/Apps`, apps)
      .pipe(retry(2))
      .subscribe(
        data => {
          let successMsg: string;
          this._translateService
            .get('USERS_SERVICE.success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(value => {
              successMsg = value;
              if (clientId) {
                this._appUserServ.getAppUsers(clientId);
              }
              this._userApps.next(apps);
            });
          if (dialog) {
            dialog.close();
          }
          if (!ignoreMessage) {
            let dispMsg = '';
            if (userName) {
              this._translateService
                .get('SNACKBAR.user_app_access',{
                  userName: userName
                })
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (dispMsg = value));
            } else {
            this._translateService
              .get('SNACKBAR.user_added_success')
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((value) => (dispMsg = value));
            }
            this.loading = false;
            this._snackbarService.showSnackbar(
              SnackbarType.success,
              successMsg,
              dispMsg
            );
          }
        },
        error => this._handleSaveAppsError(error, userName)
      );
  }

  private _handleSaveAppsError(
    error: HttpErrorResponse,
    userName?: string
  ): void {
    this.loading =false;
    let savingErrorMsg: string;
    let errorTitle: string;
    this._translateService
      .get('ERROR_SAVING')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => {
        if (userName) {
          savingErrorMsg = `${value} for ${userName}`; //append message
        } else {
          savingErrorMsg = value;
        }
      });
    if (error.error instanceof ErrorEvent) {
      console.error(`[user-apps-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[user-apps-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }

    this._translateService
      .get('SNACKBAR.error')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorTitle = value));
    this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, savingErrorMsg);
    this._error.next(savingErrorMsg);
  }
}
